import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { api } from '../lib/api'
import { useAsync } from '../lib/store'
import { ErrorNote, Fill, Loading, Shell } from '../components/Layout'

const CATEGORIES = ['All', 'Ingredients', 'Routines', 'Skin concerns', 'Sensitive skin']

export function Journal() {
  const [cat, setCat] = useState('All')
  const { data, loading, error, reload } = useAsync(() => api.posts(cat), [cat])
  const { data: faqs } = useAsync(() => api.faqs(), [])
  const [open, setOpen] = useState(0)

  const posts = data ?? []
  const [lead, ...rest] = posts

  return (
    <Shell>
      <section className="section-split" style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(280px,0.8fr)', background: 'var(--sand)' }}>
        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 18, padding: 'clamp(28px,4.4vw,68px)' }}>
          <div className="eyebrow">The Lumera Journal</div>
          <h1 className="display" style={{ fontSize: 'clamp(30px,3.6vw,52px)', maxWidth: '16ch' }}>
            Skincare advice, without the marketing.
          </h1>
          <p className="body-copy" style={{ maxWidth: '38ch' }}>
            Ingredient education, honest routines and the reasoning behind them. Written by the people who formulate what we sell.
          </p>
        </div>
        <div style={{ position: 'relative', minHeight: 'clamp(220px,26vw,380px)' }}>
          <Fill src={lead?.image} alt={lead?.title ?? 'Lumera Journal'} label="Editorial" />
        </div>
      </section>

      <section className="section-tight" style={{ paddingBottom: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 20, flexWrap: 'wrap', borderBottom: '1px solid var(--line)', paddingBottom: 18 }}>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {CATEGORIES.map((c) => (
              <button key={c} className="chip" aria-pressed={cat === c} onClick={() => setCat(c)}>
                {c}
              </button>
            ))}
          </div>
          <div style={{ fontSize: 11, color: 'var(--muted-2)', letterSpacing: '.06em' }}>
            {posts.length} {posts.length === 1 ? 'article' : 'articles'}
          </div>
        </div>
      </section>

      <section className="section" style={{ paddingTop: 'clamp(22px,2.6vw,34px)' }}>
        {loading ? (
          <Loading label="Loading articles" />
        ) : error ? (
          <ErrorNote message={error} onRetry={reload} />
        ) : posts.length === 0 ? (
          <p className="small" style={{ textAlign: 'center' }}>
            No articles in this category yet.
          </p>
        ) : (
          <>
            {lead && (
              <Link
                to={`/journal/${lead.slug}`}
                className="section-split"
                style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.2fr) minmax(0,1fr)', gap: 'clamp(16px,2.4vw,34px)', background: 'var(--stone)', marginBottom: 'clamp(18px,2.4vw,32px)', alignItems: 'stretch' }}
              >
                <span style={{ position: 'relative', display: 'block', aspectRatio: '16 / 10', overflow: 'hidden' }}>
                  <Fill src={lead.image} alt={lead.title} label={lead.category} />
                </span>
                <span style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 12, padding: 'clamp(20px,2.6vw,40px)' }}>
                  <span style={{ fontSize: 9.5, letterSpacing: '.2em', textTransform: 'uppercase', color: 'var(--faint)' }}>
                    {lead.category} · {lead.readMinutes} min read
                  </span>
                  <span className="display" style={{ fontSize: 'clamp(20px,2.3vw,30px)', lineHeight: 1.25 }}>
                    {lead.title}
                  </span>
                  <span style={{ fontSize: 13, fontWeight: 300, lineHeight: 1.8, color: 'var(--muted)' }}>{lead.dek}</span>
                  <span style={{ fontSize: 11, letterSpacing: '.1em', color: 'var(--muted)' }}>Read more →</span>
                </span>
              </Link>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', gap: 'clamp(12px,1.6vw,22px)' }}>
              {rest.map((post) => (
                <Link key={post.slug} to={`/journal/${post.slug}`} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <span style={{ position: 'relative', display: 'block', aspectRatio: '4 / 3', overflow: 'hidden' }}>
                    <Fill src={post.image} alt={post.title} label={post.category} />
                  </span>
                  <span style={{ fontSize: 9.5, letterSpacing: '.2em', textTransform: 'uppercase', color: 'var(--faint)' }}>
                    {post.category} · {post.readMinutes} min
                  </span>
                  <span style={{ fontSize: 13, fontWeight: 400, lineHeight: 1.55 }}>{post.title}</span>
                  <span style={{ fontSize: 12, fontWeight: 300, lineHeight: 1.7, color: 'var(--muted)' }}>{post.dek}</span>
                </Link>
              ))}
            </div>
          </>
        )}
      </section>

      <section id="faqs" className="section" style={{ background: 'var(--oat)' }}>
        <h2 className="rule-head">Frequently asked</h2>
        <div style={{ maxWidth: 760, margin: '0 auto', display: 'flex', flexDirection: 'column' }}>
          {(faqs ?? []).map((f, i) => (
            <div key={f.id} style={{ borderBottom: '1px solid #ddd5cd' }}>
              <button
                onClick={() => setOpen(open === i ? -1 : i)}
                aria-expanded={open === i}
                style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, background: 'none', border: 'none', cursor: 'pointer', padding: '18px 0', textAlign: 'left', fontSize: 13.5, fontWeight: 400, color: 'var(--ink)' }}
              >
                {f.question}
                <span style={{ fontSize: 17, color: 'var(--muted)', flex: '0 0 auto' }}>{open === i ? '–' : '+'}</span>
              </button>
              {open === i && (
                <p className="fade" style={{ margin: '0 0 20px', fontSize: 13, fontWeight: 300, lineHeight: 1.85, color: '#4b423b', maxWidth: '68ch' }}>
                  {f.answer}
                </p>
              )}
            </div>
          ))}
        </div>
      </section>
    </Shell>
  )
}

export function JournalPost() {
  const { slug = '' } = useParams()
  const { data, loading, error, reload } = useAsync(() => api.post(slug), [slug])
  const { data: more } = useAsync(() => api.posts(), [])

  if (loading) return <Shell><Loading label="Loading article" /></Shell>
  if (error || !data) return <Shell><ErrorNote message={error ?? 'Article not found.'} onRetry={reload} /></Shell>

  const related = (more ?? []).filter((p) => p.slug !== data.slug).slice(0, 3)

  return (
    <Shell>
      <article>
        <header className="section" style={{ maxWidth: 780, margin: '0 auto', textAlign: 'center', paddingBottom: 'clamp(20px,2.4vw,32px)' }}>
          <div style={{ fontSize: 9.5, letterSpacing: '.2em', textTransform: 'uppercase', color: 'var(--faint)', marginBottom: 16 }}>
            {data.category} · {data.readMinutes} min read
          </div>
          <h1 className="display" style={{ fontSize: 'clamp(28px,3.4vw,46px)', lineHeight: 1.18 }}>
            {data.title}
          </h1>
          <p style={{ margin: '18px 0 0', fontSize: 14, fontWeight: 300, lineHeight: 1.8, color: 'var(--muted)' }}>{data.dek}</p>
        </header>

        <div style={{ position: 'relative', aspectRatio: '21 / 9', maxWidth: 1100, margin: '0 auto', overflow: 'hidden', background: 'var(--stone)' }}>
          <Fill src={data.image} alt={data.title} label={data.category} />
        </div>

        <div className="section" style={{ maxWidth: 680, margin: '0 auto' }}>
          {(data.body ?? '').split('\n\n').filter(Boolean).map((para, i) => (
            <p key={i} style={{ margin: '0 0 22px', fontSize: 14.5, fontWeight: 300, lineHeight: 1.95, color: '#3a332e' }}>
              {para}
            </p>
          ))}

          <div style={{ borderTop: '1px solid var(--line)', paddingTop: 26, marginTop: 10, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <Link to="/journal" className="btn btn-sm">
              All articles
            </Link>
            <Link to="/quiz" className="btn btn-sm">
              Take the skin quiz
            </Link>
          </div>
        </div>
      </article>

      {related.length > 0 && (
        <section className="section" style={{ background: 'var(--oat)' }}>
          <h2 className="rule-head">Keep reading</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', gap: 'clamp(12px,1.6vw,22px)' }}>
            {related.map((post) => (
              <Link key={post.slug} to={`/journal/${post.slug}`} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <span style={{ position: 'relative', display: 'block', aspectRatio: '4 / 3', overflow: 'hidden' }}>
                  <Fill src={post.image} alt={post.title} label={post.category} />
                </span>
                <span style={{ fontSize: 9.5, letterSpacing: '.2em', textTransform: 'uppercase', color: 'var(--faint)' }}>{post.category}</span>
                <span style={{ fontSize: 13, fontWeight: 400, lineHeight: 1.55 }}>{post.title}</span>
              </Link>
            ))}
          </div>
        </section>
      )}
    </Shell>
  )
}
